// Parallel-swath ("AB line") course generation. Classic guidance pattern: one baseline A→B, then
// parallel copies offset by the implement width, clipped to the field. All spacing math is done in
// projected METERS so lanes stay exactly `widthM` apart everywhere; clipping uses turf in lon/lat.

import {
  polygon as turfPolygon,
  lineString,
  midpoint,
  lineSplit,
  booleanPointInPolygon,
  buffer,
  bearing,
  centroid,
  circle as turfCircle,
  difference as turfDifference,
  featureCollection,
  area as turfArea,
} from '@turf/turf';
import type { MultiPolygon } from 'geojson';
import type { Feature, Polygon } from 'geojson';
import { projectorForLon, meanLon, type Projector } from './projection';
import { sub, dot, normalize, perpRight, type Vec2 } from './geometry';
import type { LonLat, Course, Lane } from './types';

/** A field polygon: either an outer-ring coordinate list [lon,lat][], or a GeoJSON Polygon/Feature. */
export type FieldPolygon = LonLat[] | Polygon | Feature<Polygon>;

export interface SwathOptions {
  /** Inward turning margin at the field edge, meters (0 = none). */
  headlandM?: number;
  /** Safety cap on the number of lane segments produced. */
  maxLanes?: number;
}

function toPolygonFeature(input: FieldPolygon): Feature<Polygon> {
  if (Array.isArray(input)) {
    const ring = input.slice();
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first && last && (first[0] !== last[0] || first[1] !== last[1])) ring.push(first);
    return turfPolygon([ring as number[][]]);
  }
  if (input.type === 'Feature') return input;
  return { type: 'Feature', properties: {}, geometry: input };
}

function outerRing(poly: Feature<Polygon>): LonLat[] {
  return (poly.geometry.coordinates[0] ?? []) as LonLat[];
}

function clipLineToPolygon(line: Feature, poly: Feature<Polygon>): LonLat[][] {
  let pieces: Feature[] = [];
  try {
    pieces = lineSplit(line as never, poly as never).features as Feature[];
  } catch {
    pieces = [];
  }
  if (pieces.length === 0) pieces = [line];
  const out: LonLat[][] = [];
  for (const seg of pieces) {
    const coords = (seg.geometry as { coordinates?: LonLat[] }).coordinates;
    if (!coords || coords.length < 2) continue;
    const a = coords[0]!;
    const b = coords[coords.length - 1]!;
    const mid = midpoint(a, b);
    if (booleanPointInPolygon(mid, poly)) out.push(coords);
  }
  return out;
}

/**
 * Generate parallel driving lanes for `field`, spaced `widthM` apart, parallel to the A→B baseline,
 * clipped to the field. Lane index 0 is the AB line; +k is to the right of A→B, −k to the left.
 */
export function generateSwaths(
  field: FieldPolygon,
  a: LonLat,
  b: LonLat,
  widthM: number,
  opts: SwathOptions = {},
): Course {
  if (!(widthM > 0)) throw new Error(`widthM must be > 0, got ${widthM}`);

  const poly = toPolygonFeature(field);
  const ring = outerRing(poly);
  if (ring.length < 4) throw new Error('field polygon needs at least 3 distinct vertices');

  // Optional headland: shrink the field inward; fall back to the full field if it collapses.
  let workPoly = poly;
  if (opts.headlandM && opts.headlandM > 0) {
    const shrunk = buffer(poly, -opts.headlandM, { units: 'meters' });
    if (shrunk && shrunk.geometry.type === 'Polygon') workPoly = shrunk as Feature<Polygon>;
  }

  const proj: Projector = projectorForLon(meanLon(ring));
  const A = proj.toMeters(a);
  const B = proj.toMeters(b);
  const u = normalize(sub(B, A)); // heading direction (meters)
  if (u[0] === 0 && u[1] === 0) throw new Error('A and B coincide; cannot define a heading');
  const n = perpRight(u); // +offset is to the right of travel

  // Span of the field along the perpendicular (how many lanes) and along-track (how long to draw).
  const ringM = ring.map((c) => proj.toMeters(c));
  let minD = Infinity;
  let maxD = -Infinity;
  let minT = Infinity;
  let maxT = -Infinity;
  for (const v of ringM) {
    const r = sub(v, A);
    const d = dot(r, n);
    const t = dot(r, u);
    if (d < minD) minD = d;
    if (d > maxD) maxD = d;
    if (t < minT) minT = t;
    if (t > maxT) maxT = t;
  }

  const pad = widthM * 2 + 10;
  const tStart = minT - pad;
  const tEnd = maxT + pad;
  const kMin = Math.floor(minD / widthM);
  const kMax = Math.ceil(maxD / widthM);

  const lanes: Lane[] = [];
  for (let k = kMin; k <= kMax; k++) {
    if (opts.maxLanes && lanes.length >= opts.maxLanes) break;
    const off = k * widthM;
    const p0: Vec2 = [A[0] + n[0] * off + u[0] * tStart, A[1] + n[1] * off + u[1] * tStart];
    const p1: Vec2 = [A[0] + n[0] * off + u[0] * tEnd, A[1] + n[1] * off + u[1] * tEnd];
    const line = lineString([proj.toLonLat(p0), proj.toLonLat(p1)]);
    for (const seg of clipLineToPolygon(line, workPoly)) {
      lanes.push({ index: k, coordinates: seg });
    }
  }
  lanes.sort((x, y) => x.index - y.index);

  let hdg = bearing(a, b);
  if (hdg < 0) hdg += 360;

  return { lanes, widthM, headingDeg: hdg, epsg: proj.epsg, ab: { a, b } };
}

/** A circular no-drive zone (e.g. around a windmill or pole). */
export interface AvoidCircle {
  center: LonLat;
  radiusM: number;
}

/**
 * Cut circular avoidance zones out of a field, returning a polygon (with holes) ready for
 * generateSwaths — lanes then clip around the zones automatically. If a zone splits the field into
 * pieces, the largest piece is kept.
 */
export function subtractCircles(field: FieldPolygon, circles: AvoidCircle[]): Feature<Polygon> {
  let poly: Feature<Polygon | MultiPolygon> = toPolygonFeature(field);
  for (const c of circles) {
    if (!(c.radiusM > 0)) continue;
    const disc = turfCircle(c.center, c.radiusM, { units: 'meters', steps: 48 });
    const diff = turfDifference(featureCollection([poly, disc]));
    if (diff) poly = diff;
  }
  if (poly.geometry.type === 'MultiPolygon') {
    let best: number[][][] | null = null;
    let bestArea = -1;
    for (const coords of poly.geometry.coordinates) {
      const part = turfPolygon(coords);
      const a = turfArea(part);
      if (a > bestArea) {
        bestArea = a;
        best = coords;
      }
    }
    return turfPolygon(best ?? [[]]);
  }
  return poly as Feature<Polygon>;
}

/**
 * The headland boundary ring(s): the field shrunk inward by `headlandM` (the turning margin). Returns
 * the inner-boundary ring(s) as lon/lat polylines for drawing. Empty if the margin eats the field.
 */
export function headlandRing(field: FieldPolygon, headlandM: number): LonLat[][] {
  if (!(headlandM > 0)) return [];
  const shrunk = buffer(toPolygonFeature(field), -headlandM, { units: 'meters' });
  if (!shrunk) return [];
  const g = shrunk.geometry;
  if (g.type === 'Polygon') return [(g.coordinates[0] ?? []) as LonLat[]];
  if (g.type === 'MultiPolygon') return g.coordinates.map((c) => (c[0] ?? []) as LonLat[]);
  return [];
}

/** True bearing (deg) of a field's longest edge — a sensible default AB direction. */
export function longestEdgeBearing(field: FieldPolygon): number {
  const ring = outerRing(toPolygonFeature(field));
  const proj = projectorForLon(meanLon(ring));
  let bestLen = -1;
  let bestBearing = 0;
  for (let i = 0; i + 1 < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[i + 1]!;
    const am = proj.toMeters(a);
    const bm = proj.toMeters(b);
    const l = Math.hypot(bm[0] - am[0], bm[1] - am[1]);
    if (l > bestLen) {
      bestLen = l;
      let hdg = bearing(a, b);
      if (hdg < 0) hdg += 360;
      bestBearing = hdg;
    }
  }
  return bestBearing;
}

/** True bearings (deg, [0,360)) of every edge of a field — the sensible angles to snap a course to. */
export function edgeBearings(field: FieldPolygon): number[] {
  const ring = outerRing(toPolygonFeature(field));
  const out: number[] = [];
  for (let i = 0; i + 1 < ring.length; i++) {
    let b = bearing(ring[i]!, ring[i + 1]!);
    if (b < 0) b += 360;
    out.push(b);
  }
  return out;
}

/**
 * Build an A→B baseline through the field centroid at the given true bearing (defaults to the
 * longest-edge direction). The line is long enough to span the field; generateSwaths re-clips it.
 */
export function abFromField(field: FieldPolygon, headingDeg?: number): { a: LonLat; b: LonLat } {
  const poly = toPolygonFeature(field);
  const ring = outerRing(poly);
  const proj = projectorForLon(meanLon(ring));
  const c = centroid(poly).geometry.coordinates as LonLat;
  const cm = proj.toMeters(c);
  const hdg = headingDeg ?? longestEdgeBearing(field);
  const rad = (hdg * Math.PI) / 180;
  // bearing: 0=N, 90=E. Direction unit vector in meters (E, N): (sin, cos).
  const u: Vec2 = [Math.sin(rad), Math.cos(rad)];
  // Half-length: field diagonal, generous.
  let maxR = 0;
  for (const v of ring) {
    const vm = proj.toMeters(v);
    maxR = Math.max(maxR, Math.hypot(vm[0] - cm[0], vm[1] - cm[1]));
  }
  const half = maxR + 10;
  const a = proj.toLonLat([cm[0] - u[0] * half, cm[1] - u[1] * half]);
  const b = proj.toLonLat([cm[0] + u[0] * half, cm[1] + u[1] * half]);
  return { a, b };
}

export interface CourseStats {
  /** Number of lanes (passes). */
  lanes: number;
  /** Direction changes (≈ lanes − 1). */
  turns: number;
  /** Total drivable lane length, meters. */
  totalLengthM: number;
  /** Rough time estimate, minutes (default 2.5 m/s + 20 s/turn). */
  estMinutes: number;
}

/** Distance/turn/time stats for a generated course. */
export function courseStats(course: Course, speedMps = 2.5, secPerTurn = 20): CourseStats {
  const proj = projectorForLon(meanLon([course.ab.a, course.ab.b]));
  let total = 0;
  for (const lane of course.lanes) {
    const pts = lane.coordinates.map((c) => proj.toMeters(c));
    for (let i = 0; i + 1 < pts.length; i++) {
      total += Math.hypot(pts[i + 1]![0] - pts[i]![0], pts[i + 1]![1] - pts[i]![1]);
    }
  }
  const lanes = new Set(course.lanes.map((l) => l.index)).size;
  const turns = Math.max(0, lanes - 1);
  const estMinutes = (total / Math.max(0.1, speedMps) + turns * secPerTurn) / 60;
  return { lanes, turns, totalLengthM: total, estMinutes };
}

export interface BestDirection {
  headingDeg: number;
  stats: CourseStats;
}

/**
 * Pick the AB direction that minimises turns (then total length) — i.e. the fewest, longest passes,
 * so the most driving is on straight lines. Tries the field's edge bearings plus a coarse sweep.
 */
export function bestDirection(field: FieldPolygon, widthM: number, headlandM = 0): BestDirection {
  const candidates = new Set<number>();
  for (const b of edgeBearings(field)) candidates.add(((Math.round(b) % 360) + 360) % 360);
  for (let a = 0; a < 180; a += 15) candidates.add(a);

  let best: BestDirection | null = null;
  for (const hdg of candidates) {
    const ab = abFromField(field, hdg);
    const course = generateSwaths(field, ab.a, ab.b, widthM, headlandM > 0 ? { headlandM } : {});
    if (course.lanes.length === 0) continue;
    const stats = courseStats(course);
    if (
      !best ||
      stats.turns < best.stats.turns ||
      (stats.turns === best.stats.turns && stats.totalLengthM < best.stats.totalLengthM)
    ) {
      best = { headingDeg: hdg, stats };
    }
  }
  return best ?? { headingDeg: 0, stats: { lanes: 0, turns: 0, totalLengthM: 0, estMinutes: 0 } };
}

/**
 * Boundary-parallel ("contour") course: concentric inward rings of the field, spaced `widthM`. Each
 * ring follows the field's shape — good for curved/irregular fields. Lane 0 is the boundary itself.
 */
export function contourCourse(field: FieldPolygon, widthM: number, maxRings = 500): Course {
  if (!(widthM > 0)) throw new Error(`widthM must be > 0, got ${widthM}`);
  const ring0 = outerRing(toPolygonFeature(field));
  const lanes: Lane[] = [{ index: 0, coordinates: ring0 }];
  for (let k = 1; k < maxRings; k++) {
    const rings = headlandRing(field, k * widthM);
    if (rings.length === 0) break;
    for (const r of rings) if (r.length >= 4) lanes.push({ index: k, coordinates: r });
  }
  const headingDeg = longestEdgeBearing(field);
  return {
    lanes,
    widthM,
    headingDeg,
    epsg: projectorForLon(meanLon(ring0)).epsg,
    ab: abFromField(field, headingDeg),
  };
}
